import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, CheckSquare, FileText, Search, X, TrendingUp, DollarSign, Clock, Wallet } from 'lucide-react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Customer } from '../components/CustomerCard';
import { Task } from '../components/TaskCard';
import { Invoice } from '../components/InvoiceCard';
import { Earning } from '../components/EarningCard';
import { Payout } from '../components/PayoutCard';
import { StatCard } from '../components/StatCard';
import { RichTextEditor } from '../components/RichTextEditor';
interface DashboardProps {
  customers: Customer[];
  tasks: Task[];
  invoices: Invoice[];
  earnings: Earning[];
  payouts: Payout[];
  onAddTask: (task: Omit<Task, 'id' | 'customerName'>) => void;
  onEditTask: (task: Task) => void;
  onViewCustomer: (customerId: string) => void;
}
export function Dashboard({
  customers,
  tasks,
  invoices,
  earnings,
  payouts,
  onEditTask,
  onViewCustomer
}: DashboardProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<'Samlet' | 'Ladeboks' | 'Andet'>('Samlet');
  const [notes, setNotes] = useState('');
  // Load notes from Firebase with real-time sync
  useEffect(() => {
    const notesDocRef = doc(db, 'settings', 'dashboard-notes');
    const unsubscribe = onSnapshot(notesDocRef, docSnapshot => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setNotes(data.content || '');
      }
    }, error => {
      console.error('Error loading notes:', error);
    });
    return () => unsubscribe();
  }, []);
  // Save notes to Firebase with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      try {
        const notesDocRef = doc(db, 'settings', 'dashboard-notes');
        await setDoc(notesDocRef, {
          content: notes,
          updatedAt: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error saving notes:', error);
      }
    }, 1000); // Debounce: save 1 second after user stops typing
    return () => clearTimeout(timeoutId);
  }, [notes]);
  // Search Logic
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return {
      customers: [],
      tasks: []
    };
    const query = searchQuery.toLowerCase();
    const matchedCustomers = customers.filter(c => c.name.toLowerCase().includes(query) || c.customerId.toLowerCase().includes(query) || c.phone?.includes(query) || c.email?.toLowerCase().includes(query)).slice(0, 5);
    const matchedTasks = tasks.filter(t => t.title.toLowerCase().includes(query) || t.taskId?.toLowerCase().includes(query) || t.customerName?.toLowerCase().includes(query)).slice(0, 5);
    return {
      customers: matchedCustomers,
      tasks: matchedTasks
    };
  }, [searchQuery, customers, tasks]);
  // Economy Calculations
  const economyStats = useMemo(() => {
    let filteredTasks = tasks;
    let filteredInvoices = invoices;
    if (selectedCategory !== 'Samlet') {
      filteredTasks = tasks.filter(t => t.category === selectedCategory);
      filteredInvoices = invoices.filter(i => i.type === selectedCategory);
    }
    const revenue = filteredTasks.reduce((sum, t) => sum + (Number(t.totalPrice) || 0), 0);
    // Calculate expenses: invoices + material costs for Ladeboks
    let expenses = filteredInvoices.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
    // Add material costs to expenses for Ladeboks category
    if (selectedCategory === 'Ladeboks' || selectedCategory === 'Samlet') {
      const materialCosts = filteredTasks.filter(t => selectedCategory === 'Samlet' || t.category === 'Ladeboks').reduce((sum, t) => sum + (Number(t.materialPrice) || 0), 0);
      expenses += materialCosts;
    }
    const profit = revenue - expenses;
    const hours = filteredTasks.reduce((sum, t) => sum + (Number(t.hours) || 0), 0);
    return {
      revenue,
      expenses,
      profit,
      hours
    };
  }, [tasks, invoices, selectedCategory]);
  const stats = [{
    title: 'Kunder',
    value: customers.length,
    icon: Users,
    iconBgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
    subtitle: 'Aktive kunder'
  }, {
    title: 'Opgaver',
    value: tasks.length,
    icon: CheckSquare,
    iconBgColor: 'bg-green-50',
    iconColor: 'text-green-600',
    subtitle: 'Åbne opgaver'
  }, {
    title: 'Fakturaer',
    value: invoices.length,
    icon: FileText,
    iconBgColor: 'bg-purple-50',
    iconColor: 'text-purple-600',
    subtitle: 'Antal fakturaer i alt'
  }];
  return <div className="space-y-12" data-id="element-1453">
      <header data-id="element-1454">
        <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1455">Overblik</h1>
        <p className="text-stone-600 mb-8" data-id="element-1456">
          Komplet overblik over din virksomhed
        </p>

        {/* Search Bar and Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1457">
          {/* Search Bar */}
          <div className="relative" data-id="element-1458">
            {/* Search Bar */}
            <div className="space-y-6" data-id="element-1459">
              <div className="relative max-w-2xl" data-id="element-1460">
                <div className="relative" data-id="element-1461">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1462" />
                  <input type="text" placeholder="Søg efter kunde eller sag..." value={searchQuery} onChange={e => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(true);
                }} onFocus={() => setShowSearchResults(true)} className="w-full pl-12 pr-12 py-4 rounded-xl border-2 border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-lg shadow-sm" data-id="element-1463" />
                  {searchQuery && <button onClick={() => {
                  setSearchQuery('');
                  setShowSearchResults(false);
                }} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-600 transition-colors" data-id="element-1464">
                      <X className="w-5 h-5" data-id="element-1465" />
                    </button>}
                </div>

                {/* Search Results Dropdown */}
                <AnimatePresence data-id="element-1466">
                  {showSearchResults && searchQuery.trim() && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute top-full mt-2 w-full bg-white rounded-xl border border-stone-200 shadow-xl z-50 overflow-hidden" data-id="element-1467">
                      {searchResults.customers.length === 0 && searchResults.tasks.length === 0 ? <div className="p-4 text-center text-stone-500" data-id="element-1468">
                          Ingen resultater fundet
                        </div> : <div className="max-h-[400px] overflow-y-auto" data-id="element-1469">
                          {searchResults.customers.length > 0 && <div className="p-2" data-id="element-1470">
                              <h3 className="px-3 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wider" data-id="element-1471">
                                Kunder
                              </h3>
                              {searchResults.customers.map(customer => <button key={customer.id} onClick={() => {
                        onViewCustomer(customer.id!);
                        setShowSearchResults(false);
                        setSearchQuery('');
                      }} className="w-full flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg transition-colors text-left" data-id="element-1472">
                                  <div className="p-2 bg-blue-50 rounded-lg" data-id="element-1473">
                                    <Users className="w-4 h-4 text-blue-600" data-id="element-1474" />
                                  </div>
                                  <div data-id="element-1475">
                                    <p className="font-medium text-stone-900" data-id="element-1476">
                                      {customer.name}
                                    </p>
                                    <p className="text-sm text-stone-500" data-id="element-1477">
                                      {customer.customerId}
                                    </p>
                                  </div>
                                </button>)}
                            </div>}

                          {searchResults.tasks.length > 0 && <div className="p-2 border-t border-stone-100" data-id="element-1478">
                              <h3 className="px-3 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wider" data-id="element-1479">
                                Sager
                              </h3>
                              {searchResults.tasks.map(task => <button key={task.id} onClick={() => {
                        onEditTask(task);
                        setShowSearchResults(false);
                        setSearchQuery('');
                      }} className="w-full flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg transition-colors text-left" data-id="element-1480">
                                  <div className="p-2 bg-green-50 rounded-lg" data-id="element-1481">
                                    <CheckSquare className="w-4 h-4 text-green-600" data-id="element-1482" />
                                  </div>
                                  <div data-id="element-1483">
                                    <p className="font-medium text-stone-900" data-id="element-1484">
                                      {task.title}
                                    </p>
                                    <p className="text-sm text-stone-500" data-id="element-1485">
                                      {task.taskId} • {task.customerName}
                                    </p>
                                  </div>
                                </button>)}
                            </div>}
                        </div>}
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Notes Field */}

          {/* Notes Field */}
          <div className="max-w-4xl" data-id="element-1486">
            <RichTextEditor label="Noter" value={notes} onChange={setNotes} placeholder="Skriv dine noter her..." rows={2} data-id="element-1487" />
          </div>
          {/* Notes Field */}
        </div>
      </header>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1488">
        {stats.map((stat, index) => <StatCard key={stat.title} index={index} title={stat.title} value={stat.value} icon={stat.icon} iconBgColor={stat.iconBgColor} iconColor={stat.iconColor} subtitle={stat.subtitle} data-id="element-1489" />)}
      </div>

      {/* Economy Section */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-id="element-1490">
        <div className="p-6 border-b border-stone-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1491">
          <h2 className="text-xl font-bold text-stone-900" data-id="element-1492">Økonomi</h2>

          <div className="flex p-1 bg-stone-100 rounded-lg" data-id="element-1493">
            {(['Samlet', 'Ladeboks', 'Andet'] as const).map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${selectedCategory === category ? 'bg-white text-teal-700 shadow-sm' : 'text-stone-600 hover:text-stone-900'}`} data-id="element-1494">
                {category === 'Andet' ? 'Alm. Installation' : category}
              </button>)}
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1495">
          {/* Revenue */}
          <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100" data-id="element-1496">
            <div className="flex items-center gap-3 mb-2" data-id="element-1497">
              <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-1498">
                <TrendingUp className="w-5 h-5 text-emerald-600" data-id="element-1499" />
              </div>
              <span className="text-sm font-medium text-emerald-900" data-id="element-1500">
                Omsætning
              </span>
            </div>
            <p className="text-2xl font-bold text-emerald-700" data-id="element-1501">
              {economyStats.revenue.toLocaleString('da-DK')} kr
            </p>
          </div>

          {/* Expenses */}
          <div className="p-4 rounded-xl bg-red-50 border border-red-100" data-id="element-1502">
            <div className="flex items-center gap-3 mb-2" data-id="element-1503">
              <div className="p-2 bg-red-100 rounded-lg" data-id="element-1504">
                <Wallet className="w-5 h-5 text-red-600" data-id="element-1505" />
              </div>
              <span className="text-sm font-medium text-red-900" data-id="element-1506">Udgifter</span>
            </div>
            <p className="text-2xl font-bold text-red-700" data-id="element-1507">
              {economyStats.expenses.toLocaleString('da-DK')} kr
            </p>
          </div>

          {/* Profit */}
          <div className="p-4 rounded-xl bg-blue-50 border border-blue-100" data-id="element-1508">
            <div className="flex items-center gap-3 mb-2" data-id="element-1509">
              <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1510">
                <DollarSign className="w-5 h-5 text-blue-600" data-id="element-1511" />
              </div>
              <span className="text-sm font-medium text-blue-900" data-id="element-1512">
                Fortjeneste
              </span>
            </div>
            <p className="text-2xl font-bold text-blue-700" data-id="element-1513">
              {economyStats.profit.toLocaleString('da-DK')} kr
            </p>
          </div>

          {/* Hours */}
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-100" data-id="element-1514">
            <div className="flex items-center gap-3 mb-2" data-id="element-1515">
              <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1516">
                <Clock className="w-5 h-5 text-amber-600" data-id="element-1517" />
              </div>
              <span className="text-sm font-medium text-amber-900" data-id="element-1518">Timer</span>
            </div>
            <p className="text-2xl font-bold text-amber-700" data-id="element-1519">
              {economyStats.hours.toLocaleString('da-DK')} t
            </p>
          </div>
        </div>
      </div>
    </div>;
}